import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Components/Redux/store";
import { setAuth } from "../Components/Redux/authSlice";
import { FaMoneyBillWave, FaPlus, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "./fees.css";

interface FeeStructure {
  id: number;
  fee_name: string;
  amount: string;
}

const FeeStructureComponent: React.FC = () => {
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [currentFee, setCurrentFee] = useState<FeeStructure | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie("token");
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        const response = await axios.get("/users/user");
        setUserRole(response.data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error("Error fetching user data:", error);
        dispatch(setAuth(false));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    if (auth) {
      fetchFeeStructures();
    }
  }, [auth]);

  const fetchFeeStructures = async () => {
    try {
      const response = await axios.get("/fees/fee-structures/");
      setFeeStructures(response.data);
    } catch (error) {
      console.error("Error fetching fee structures:", error);
      setErrorMessage("Ku guuldareystay in la soo dejiyo noocyada lacagaha");
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCurrentFee((prev) => ({
      ...prev!,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && currentFee) {
        await axios.put(`/fees/fee-structures/${currentFee.id}/`, currentFee);
        setSuccessMessage("Si guul leh ayaa loo cusbooneysiiyay nooca lacagta!");
      } else {
        await axios.post("/fees/fee-structures/", currentFee);
        setSuccessMessage("Si guul leh ayaa loo kordhiyay nooca lacagta!");
      }
      setShowModal(false);
      fetchFeeStructures();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error saving fee structure:", error);
      setErrorMessage("Ku guuldareystay in la keydiyo nooca lacagta");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Ma hubtaa inaad rabto inaad tirtirto noocan lacagta?")) {
      try {
        await axios.delete(`/fees/fee-structures/${id}/`);
        fetchFeeStructures();
        setSuccessMessage("Si guul leh ayaa loo tirtiray nooca lacagta!");
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error("Error deleting fee structure:", error);
        setErrorMessage("Ku guuldareystay in la tirtiro nooca lacagta");
        setTimeout(() => setErrorMessage(null), 3000);
      }
    }
  };

  const openModal = (fee: FeeStructure | null = null) => {
    setCurrentFee(fee || { id: 0, fee_name: "", amount: "" });
    setIsEditing(!!fee);
    setShowModal(true);
  };

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(";");
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return "";
  };

  if (loading) {
    return (
      <div className="loading-state" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
        <p>Soo dejinaya noocyada lacagaha...</p>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="auth-error">
        <h4>Ma aadan xaqiijin</h4>
        <p>Fadlan soo gal si aad u hesho boggan</p>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="fees-container">
        {/* Header Section */}
        <div className="fees-header">
          <div className="header-content">
            <div className="header-title">
              <FaMoneyBillWave className="header-icon" />
              <div>
                <h2>Noocyada Lacagaha</h2>
                <p className="header-subtitle">Maamul noocyada lacagaha iyo qiimaha</p>
              </div>
            </div>
            {userRole === "admin" && (
              <div className="header-actions">
                <button
                  onClick={() => openModal()}
                  className="btn-primary-modern"
                >
                  <FaPlus /> Ku Dar Nooc Lacag
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Statistics Card */}
        <div className="stats-grid">
          <div className="stat-card stat-card-blue">
            <div className="stat-icon-wrapper">
              <FaMoneyBillWave className="stat-icon" />
            </div>
            <div className="stat-content">
              <p className="stat-label">Wadarta Noocyada Lacagta</p>
              <h3 className="stat-value">{feeStructures.length}</h3>
              <span className="stat-trend">Noocyada shaqeynaya</span>
            </div>
          </div>

          <div className="stat-card stat-card-green">
            <div className="stat-icon-wrapper">
              <FaCheckCircle className="stat-icon" />
            </div>
            <div className="stat-content">
              <p className="stat-label">Celceliska Lacagta</p>
              <h3 className="stat-value">
                KSh {feeStructures.length > 0
                  ? Math.round(feeStructures.reduce((sum, fee) => sum + parseFloat(fee.amount), 0) / feeStructures.length).toLocaleString()
                  : 0
                }
              </h3>
              <span className="stat-trend">Nooc walba</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && (
          <div className="alert-success" style={{ marginBottom: '2rem' }}>
            <FaCheckCircle />
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="alert-error" style={{ marginBottom: '2rem' }}>
            <FaTimesCircle />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Table Container */}
        <div className="table-container">
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Magaca Lacagta</th>
                  <th>Lacagta (KSh)</th>
                  {userRole === "admin" && <th>Ficilada</th>}
                </tr>
              </thead>
              <tbody>
                {feeStructures.length === 0 ? (
                  <tr>
                    <td colSpan={userRole === "admin" ? 4 : 3} className="empty-state">
                      <FaMoneyBillWave />
                      <p>Lama helin noocyo lacag ah</p>
                      <small style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        Ku dar nooc lacag si aad u bilowdo
                      </small>
                    </td>
                  </tr>
                ) : (
                  feeStructures.map((fee, index) => (
                    <tr key={fee.id} className="table-row">
                      <td>{index + 1}</td>
                      <td className="student-name">{fee.fee_name}</td>
                      <td className="amount">KSh {parseFloat(fee.amount).toLocaleString()}</td>
                      {userRole === "admin" && (
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-action btn-view"
                              onClick={() => openModal(fee)}
                              title="Wax ka bedel"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn-action btn-delete"
                              onClick={() => handleDelete(fee.id)}
                              title="Tirtir"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal for Add/Edit */}
        {userRole === "admin" && (
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            className="fee-modal"
          >
            <Modal.Header closeButton className="modal-header-custom">
              <Modal.Title className="modal-title-custom">
                <FaMoneyBillWave style={{ marginRight: '0.5rem' }} />
                {isEditing ? "Wax ka bedel Nooca Lacagta" : "Ku Dar Nooc Lacag"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label">
                    Magaca Lacagta <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="fee_name"
                    className="form-input"
                    value={currentFee?.fee_name || ""}
                    onChange={handleInputChange}
                    required
                    placeholder="t.usaale, Lacagta Waxbarashada"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label">
                    Lacagta (KSh) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    className="form-input"
                    value={currentFee?.amount || ""}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Geli lacagta"
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '10px',
                      fontWeight: 600
                    }}
                  >
                    Ka Noqo
                  </Button>
                  <button
                    type="submit"
                    className="btn-submit"
                    style={{ padding: '0.75rem 1.5rem' }}
                  >
                    <FaCheckCircle />
                    {isEditing ? "Cusbooneysii" : "Keydi"}
                  </button>
                </div>
              </form>
            </Modal.Body>
          </Modal>
        )}

        <style>{`
          .fee-modal .modal-content {
            border-radius: 16px;
            border: none;
          }
          
          .modal-header-custom {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 16px 16px 0 0;
            padding: 1.5rem;
          }
          
          .modal-title-custom {
            font-weight: 700;
            display: flex;
            align-items: center;
          }
          
          .modal-body-custom {
            padding: 2rem;
          }
          
          .modal-header-custom .btn-close {
            filter: brightness(0) invert(1);
          }
        `}</style>
      </div>
    </div>
  );
};

export default FeeStructureComponent;